import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const SANITIZE_OPTS = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: [
    'allow', 'allowfullscreen', 'frameborder', 'scrolling',
    'src', 'width', 'height', 'title', 'data-youtube-video', 'referrerpolicy',
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|data|blob|filesystem):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

// Fill in missing alt attributes on <img> tags before sanitisation so every
// image in a post ships with SOME alt text — Semrush flagged 3 missing-ALT
// warnings in the last audit. We default to the post title (passed as prop).
const _ensureImgAlt = (html, fallback) => {
  if (!html || html.indexOf('<img') === -1) return html;
  const safe = String(fallback || 'Paisley article image').replace(/"/g, '&quot;').slice(0, 200);
  return html.replace(/<img\b([^>]*)>/gi, (match, attrs) => {
    if (/\balt\s*=/i.test(attrs)) return match;
    return `<img${attrs} alt="${safe}">`;
  });
};

// Rewrite any Skiddle affiliate tag to Brian's own `sktag=24907`.
// Skiddle links look like: https://www.skiddle.com/events/12345/?sktag=15755
// (someone else's tag). This regex sub covers both `?sktag=` and `&sktag=`
// query-string positions, adds a new `?sktag=24907` if the URL has no query,
// or appends `&sktag=24907` if it has a query but no sktag. Applied at
// render time so historical article HTML gets fixed without a DB migration.
const SKIDDLE_AFFILIATE_ID = '24907';
const _rewriteSkiddleTag = (html) => {
  if (!html || html.indexOf('skiddle.com') === -1) return html;
  return html.replace(/https?:\/\/(?:www\.)?skiddle\.com\/[^"'\s<>]+/gi, (url) => {
    // Case 1: sktag already present — replace its value
    if (/[?&]sktag=/i.test(url)) {
      return url.replace(/([?&]sktag=)[^&"'\s<>]*/i, `$1${SKIDDLE_AFFILIATE_ID}`);
    }
    // Case 2: URL has a query string but no sktag — append
    if (url.indexOf('?') !== -1) {
      return `${url}&sktag=${SKIDDLE_AFFILIATE_ID}`;
    }
    // Case 3: no query at all — add one
    return `${url}?sktag=${SKIDDLE_AFFILIATE_ID}`;
  });
};

const PostContent = ({ content, fallbackAlt }) => {
  const ref = useRef(null);
  const [images, setImages] = useState([]);   // [{ src, alt }, ...]
  const [open, setOpen] = useState(null);     // index | null

  // Build the image list whenever post content changes. This populates the
  // "N images" hint AND the lightbox catalogue.
  const rebuild = useCallback(() => {
    const root = ref.current;
    if (!root) return [];
    const imgs = Array.from(root.querySelectorAll('img'));
    const real = imgs.filter((img) => {
      const w = img.naturalWidth || parseInt(img.getAttribute('width') || '0', 10);
      const h = img.naturalHeight || parseInt(img.getAttribute('height') || '0', 10);
      return !(w && w < 32) && !(h && h < 32);
    });
    return real;
  }, []);

  useEffect(() => {
    // Defer one tick so dangerouslySetInnerHTML has fully painted.
    const t = setTimeout(() => {
      const real = rebuild();
      setImages(real.map((img) => ({
        src: img.currentSrc || img.src,
        alt: img.getAttribute('alt') || '',
      })));
    }, 0);
    return () => clearTimeout(t);
  }, [content, rebuild]);

  // Click handler: query DOM live at click time, so DOM/state drift is impossible.
  const onWrapperClick = useCallback((e) => {
    const t = e.target;
    let img = null;
    if (t && t.tagName === 'IMG') img = t;
    else if (t && t.closest) {
      const link = t.closest('a');
      if (link) img = link.querySelector('img');
    }
    if (!img) return;

    const real = rebuild();
    const idx = real.indexOf(img);
    if (idx < 0) return;

    // Sync state with current DOM AND open lightbox in the same render pass.
    setImages(real.map((el) => ({
      src: el.currentSrc || el.src,
      alt: el.getAttribute('alt') || '',
    })));
    setOpen(idx);
    e.preventDefault();
  }, [rebuild]);

  // Keyboard nav while lightbox is open
  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape')     setOpen(null);
      if (e.key === 'ArrowRight') setOpen((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')  setOpen((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, images.length]);

  const cur = open !== null ? images[open] : null;

  return (
    <>
      <div onClick={onWrapperClick}>
        <div
          ref={ref}
          className="prose prose-lg max-w-none post-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(_rewriteSkiddleTag(_ensureImgAlt(content || '', fallbackAlt)), SANITIZE_OPTS) }}
          style={{ color: '#334155', lineHeight: '1.8' }}
        />
      </div>

      {/* Gallery hint at the top if the post has 4+ images */}
      {images.length >= 4 && (
        <div className="mt-2 mb-6 text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="gallery-hint">
          <ZoomIn size={13} /> {images.length} images — click any to view full size
        </div>
      )}

      {/* Lightbox — portal'd to body to escape any parent stacking context */}
      {cur && createPortal(
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 select-none"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          data-testid="post-image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(null); }}
            className="absolute top-4 right-4 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          {images.length > 1 && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/80 text-xs font-medium tracking-wider">
              {open + 1} / {images.length}
            </div>
          )}

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen((i) => (i - 1 + images.length) % images.length); }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="lightbox-prev"
              aria-label="Previous image"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          <img
            src={cur.src}
            alt={cur.alt}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {cur.alt && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 max-w-[80vw] text-center text-white/85 text-sm bg-black/40 backdrop-blur-md rounded-full px-4 py-1.5">
              {cur.alt}
            </div>
          )}

          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen((i) => (i + 1) % images.length); }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="lightbox-next"
              aria-label="Next image"
            >
              <ChevronRight size={22} />
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default PostContent;

